import React, { FC } from 'react';
import { Formik, Field, FormikProps } from 'formik';
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Card,
  CardContent,
  Typography,
  FormHelperText,
  List,
  ListItem,
  ListItemIcon,
  ListItemText as MuiListItemText,
  Divider,
  Box,
} from '@mui/material';
import {
  CreateRoleRequest,
  RoleWithPermissionsResponse,
} from 'features/admin-roles/api/types';
import { PermissionResponse } from 'features/admin-permissions/api/types';
import ConfirmationBtnActionModal from 'shared/components/modals/confirmation-btn-action';
import { FormAction } from 'shared/ts-types/form-action.type';
import { ROLE_FORM_SETTINGS } from 'features/admin-roles/constants/role-form-settings.const';
import { determineFormDirtyFields } from 'shared/utils/determine-form-dirty-fields.util';
import { RoleFormValues } from 'features/admin-roles/types/role-form.type';
import { useSelector } from 'react-redux';
import { StoreState } from 'store';
import { roleFormValidationSchema } from 'features/admin-roles/schemas/role-form.schema';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { trackEvent } from 'utils/amplitude.util';
import { AmplitudeEvents } from 'shared/enums/amplitude-events.enum';

export type CreateRoleFormType = {
  handleSubmit: (values: CreateRoleRequest & { permissions: string[] }) => void;
  initialRole?: RoleWithPermissionsResponse;
  permissions: PermissionResponse[];
  action: FormAction;
};

const RolesForm: FC<CreateRoleFormType> = ({
  handleSubmit,
  initialRole,
  permissions,
  action,
}) => {
  const initialValues: RoleFormValues = {
    permissions:
      initialRole?.permissions?.map((authMethod) => authMethod.name) || [],
    name: initialRole?.name || '',
  };

  const { loading } = useSelector((state: StoreState) => state.adminRoles);

  const generateModalContent = (values: RoleFormValues, action: FormAction) => {
    const actionText =
      action === 'CREATE'
        ? 'You are about to create a new role with the following details:'
        : 'You are about to update the role with the following details:';

    const initialPermissionNames =
      initialRole?.permissions?.map((p) => p.name) || [];
    const currentPermissionNames = values.permissions;

    const addedPermissions = currentPermissionNames.filter(
      (perm) => !initialPermissionNames.includes(perm)
    );
    const removedPermissions = initialPermissionNames.filter(
      (perm) => !currentPermissionNames.includes(perm)
    );
    const unchangedPermissions = currentPermissionNames.filter((perm) =>
      initialPermissionNames.includes(perm)
    );

    const getDisplayNames = (permNames: string[]) =>
      permNames.map(
        (permName) =>
          permissions.find((p) => p.name === permName)?.name_for_display ||
          permName
      );

    const renderPermissionsList = () => {
      if (action === 'CREATE') {
        const selectedDisplayNames = getDisplayNames(currentPermissionNames);
        return (
          <Typography variant="body2" color="text.secondary">
            {selectedDisplayNames.length > 0
              ? selectedDisplayNames.join(', ')
              : 'No permissions selected'}
          </Typography>
        );
      } else {
        if (
          currentPermissionNames.length === 0 &&
          initialPermissionNames.length === 0
        ) {
          return (
            <Typography variant="body2" color="text.secondary">
              No permissions selected
            </Typography>
          );
        }

        return (
          <Box>
            {unchangedPermissions.length > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
              >
                {getDisplayNames(unchangedPermissions).join(', ')}
                {(addedPermissions.length > 0 ||
                  removedPermissions.length > 0) &&
                  ', '}
              </Typography>
            )}
            {addedPermissions.length > 0 && (
              <Typography
                variant="body2"
                color="success.main"
                component="span"
                fontWeight={'bold'}
              >
                {getDisplayNames(addedPermissions).join(', ')}
                {removedPermissions.length > 0 && ', '}
              </Typography>
            )}
            {removedPermissions.length > 0 && (
              <Typography
                variant="body2"
                color="error.main"
                component="span"
                fontWeight={'bold'}
                sx={{ textDecoration: 'line-through' }}
              >
                {getDisplayNames(removedPermissions).join(', ')}
              </Typography>
            )}
          </Box>
        );
      }
    };

    // Handle role name changes
    const renderRoleName = () => {
      if (action === 'CREATE') {
        return (
          <Typography variant="body2" color="text.secondary">
            {values.name || 'Not specified'}
          </Typography>
        );
      } else {
        const hasNameChanged = initialRole?.name !== values.name;

        if (!hasNameChanged) {
          return (
            <Typography variant="body2" color="text.secondary">
              {values.name || 'Not specified'}
            </Typography>
          );
        }

        return (
          <Box display={'flex'} flexDirection={'column'}>
            <Typography
              variant="body2"
              color="success.main"
              component="span"
              fontWeight={'bold'}
            >
              {values.name}
            </Typography>
            {initialRole?.name && (
              <Typography
                variant="body2"
                color="error.main"
                component="span"
                fontWeight={'bold'}
                sx={{ textDecoration: 'line-through' }}
              >
                {initialRole.name}
              </Typography>
            )}
          </Box>
        );
      }
    };

    return (
      <>
        <Typography gutterBottom>{actionText}</Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: '30px' }}>
              <ArrowRightIcon />
            </ListItemIcon>
            <MuiListItemText primary="Role Name" secondary={renderRoleName()} />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: '30px' }}>
              <ArrowRightIcon />
            </ListItemIcon>
            <MuiListItemText
              primary="Permissions"
              secondary={renderPermissionsList()}
            />
          </ListItem>
        </List>
        {currentPermissionNames.length === 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="warning.main">
              Warning: No permissions selected. This role will have no access
              rights.
            </Typography>
          </>
        )}
      </>
    );
  };

  return (
    <Formik<RoleFormValues>
      initialValues={initialValues}
      validationSchema={roleFormValidationSchema}
      onSubmit={(values, actions) => {
        handleSubmit(values as any);
        actions.setSubmitting(false);
      }}
      validateOnMount={true}
    >
      {({
        values,
        setFieldValue,
        errors,
        touched,
        submitForm,
        initialValues,
      }: FormikProps<RoleFormValues>) => {
        const dirtyFields = determineFormDirtyFields(initialValues, values);
        return (
          <Box sx={{ mt: '14px' }}>
            <Card sx={{ mb: '20px', maxWidth: '584px' }}>
              <CardContent sx={{ p: '20px', pb: '20px !important' }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  mb={'14px'}
                  sx={{
                    textTransform: 'lowercase',
                    fontSize: '24px',
                    fontWeight: '400',
                    '&::first-letter': {
                      textTransform: 'uppercase',
                    },
                  }}
                >
                  {action} ROLE
                </Typography>
                <FormControl fullWidth>
                  <Field
                    as={TextField}
                    label="Name"
                    name="name"
                    error={touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                    fullWidth
                  />
                  <FormHelperText sx={{ mb: '20px' }}>
                    Display name for this role
                  </FormHelperText>
                </FormControl>
                <Typography variant={'body2'}>
                  Enter a display name for this role. This name will appear
                  throughout the system. Use clear, consistent naming for all
                  your roles. For example: Admin, Member, Auditor, Compliance
                  Reviewer, Manager, or Support Agent.
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3, maxWidth: '584px' }}>
              <CardContent sx={{ p: '20px', pb: '20px !important' }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  mb={'14px'}
                  sx={{
                    fontSize: '24px',
                    fontWeight: '400',
                  }}
                >
                  Permissions
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="permissions-label">Permissions</InputLabel>
                  <Field
                    as={Select}
                    labelId="permissions-label"
                    name="permissions"
                    multiple
                    value={values.permissions}
                    onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                      const newPermissions = e.target.value as string[];
                      const previousPermissions = values.permissions;

                      // Find the toggled permission
                      const added = newPermissions.find(
                        (p) => !previousPermissions.includes(p)
                      );
                      const removed = previousPermissions.find(
                        (p) => !newPermissions.includes(p)
                      );
                      const toggledPermission = added || removed;

                      if (toggledPermission) {
                        const permissionOption = permissions.find(
                          (p) => p.name === toggledPermission
                        );
                        trackEvent(
                          AmplitudeEvents.PERMISSION_CHECKBOX_TOGGLED,
                          {
                            permission_name:
                              permissionOption?.name_for_display ||
                              toggledPermission,
                            checked: !!added,
                          }
                        );
                      }

                      setFieldValue('permissions', newPermissions);
                    }}
                    input={<OutlinedInput label="Permissions" />}
                    renderValue={(selected: string[]) =>
                      selected
                        .map(
                          (s) =>
                            permissions.find((o) => o.name === s)
                              ?.name_for_display
                        )
                        .join(', ')
                    }
                  >
                    {permissions.map((option) => (
                      <MenuItem
                        key={`${option.id}-${option.name}`}
                        value={option.name}
                      >
                        <Checkbox
                          checked={values.permissions.includes(option.name)}
                        />
                        <ListItemText primary={option.name_for_display} />
                      </MenuItem>
                    ))}
                  </Field>
                  <FormHelperText sx={{ mb: '20px' }}>
                    Select permissions
                  </FormHelperText>
                </FormControl>
                <Typography variant={'body2'}>
                  Select the permissions for this role. Choose from the list of
                  available permissions based on what this role needs to do in
                  the system. You can combine different permissions to create
                  custom roles that match your needs. For example: a Manager
                  role might have view and edit permissions, while a Member role
                  might only have view permissions.
                </Typography>
              </CardContent>
            </Card>

            <ConfirmationBtnActionModal
              onConfirm={submitForm}
              title={ROLE_FORM_SETTINGS[action].CONFIRM_MODAL_TITLE}
              confirmBtnText={ROLE_FORM_SETTINGS[action].CONFIRM_BTN_TEXT}
              isLoading={loading}
              content={generateModalContent(values, action)}
            >
              <Button
                size={'large'}
                disabled={
                  (action === 'UPDATE' && dirtyFields.length === 0) ||
                  Object.keys(errors).length > 0
                }
                variant="contained"
                color="primary"
              >
                {action} ROLE
              </Button>
            </ConfirmationBtnActionModal>
          </Box>
        );
      }}
    </Formik>
  );
};

export default RolesForm;
